// ** Modules
import { useState, useRef } from 'react';

// ** UI
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

// ** Components
import FadeInView from '@/components/common/FadeInView';

// ** Types
import type { Theme } from '@mui/material/styles';

export type VideoBlockV2Props = {
	/** Direct video file URL or any YouTube URL (youtube.com/watch, youtu.be, shorts…) */
	src: string;
	/** Optional title shown below the video (left column) */
	title?: string;
	/** Optional description shown below the video (right column) */
	description?: string;
};

const EMPTY_CAPTIONS_TRACK = 'data:text/vtt,WEBVTT';

const YOUTUBE_REGEX = /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/;

const getYouTubeId = (url: string): string | null => {
	const match = url.match(YOUTUBE_REGEX);
	return match ? match[1] : null;
};

const PlayIcon = () => (
	<Box
		sx={{
			width: { xs: 48, md: 80 },
			height: { xs: 48, md: 80 },
			borderRadius: '50%',
			bgcolor: 'rgba(255,255,255,0.95)',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			transition: 'transform 0.2s ease, background-color 0.2s ease',
			'& svg': { ml: { xs: '0.25rem', md: '0.5rem' } }
		}}
	>
		<Box
			component="svg"
			width="32"
			height="35"
			viewBox="0 0 32 35"
			fill="none"
			sx={{
				width: { xs: 19, md: 32 },
				height: { xs: 21, md: 35 }
			}}
		>
			<path
				d="M28.8 15.4C30.8 16.4 30.8 18.6 28.8 19.6L4.8 33C2.8 34 0 32.7 0 30.9V4.1C0 2.3 2.8 1 4.8 2L28.8 15.4Z"
				fill="#1E1E1E"
			/>
		</Box>
	</Box>
);

const VideoBlockV2 = ({ src, title, description }: VideoBlockV2Props) => {
	const theme = useTheme();
	const videoRef = useRef<HTMLVideoElement>(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const [youtubePlaying, setYoutubePlaying] = useState(false);

	const TWO_COL_MEDIA = `@media (min-width: ${(theme as Theme & { layout: { twoColMinWidthPx: number } }).layout.twoColMinWidthPx}px)`;

	const youtubeId = getYouTubeId(src);
	const isYoutube = Boolean(youtubeId);
	const mediaTitle = title ? `Video: ${title}` : 'Eingebettetes Video';

	const handlePlay = () => {
		const video = videoRef.current;
		if (!video) return;

		if (isPlaying) {
			video.pause();
			setIsPlaying(false);
		} else {
			video.play();
			setIsPlaying(true);
		}
	};

	const handleVideoEnded = () => {
		setIsPlaying(false);
	};

	const hasTextSection = Boolean(title || description);
	const showOverlay = isYoutube ? !youtubePlaying : !isPlaying;
	const playButtonLabel = isYoutube ? 'Video abspielen' : isPlaying ? 'Video pausieren' : 'Video abspielen';

	return (
		<FadeInView>
			<Box sx={{ py: { xs: 8, md: 12 } }}>
				<Box
					sx={{
						position: 'relative',
						borderRadius: '0.5rem',
						overflow: 'hidden',
						'&:hover .play-btn': {
							transform: 'scale(1.08)'
						}
					}}
				>
					{isYoutube ? (
						<Box
							component="iframe"
							src={
								youtubePlaying
									? `https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0`
									: `https://www.youtube.com/embed/${youtubeId}?rel=0`
							}
							title={mediaTitle}
							allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
							allowFullScreen
							sx={{
								width: '100%',
								display: 'block',
								aspectRatio: '16 / 9',
								border: 'none',
								borderRadius: '0.5rem'
							}}
						/>
					) : (
						<video
							ref={videoRef}
							src={src}
							onEnded={handleVideoEnded}
							playsInline
							style={{
								width: '100%',
								display: 'block',
								aspectRatio: '16 / 9',
								objectFit: 'cover',
								borderRadius: '0.5rem'
							}}
						>
							<track kind="captions" src={EMPTY_CAPTIONS_TRACK} srcLang="en" label="English captions" />
						</video>
					)}

					<IconButton
						onClick={isYoutube ? () => setYoutubePlaying(true) : handlePlay}
						aria-label={playButtonLabel}
						aria-pressed={isYoutube ? undefined : isPlaying}
						sx={{
							position: 'absolute',
							inset: 0,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							bgcolor: 'rgba(0,0,0,0.3)',
							opacity: showOverlay ? 1 : 0,
							transition: 'opacity 0.3s ease',
							pointerEvents: showOverlay ? 'auto' : 'none',
							borderRadius: 0,
							'&:focusVisible': {
								outline: '2px solid',
								outlineColor: 'common.white',
								outlineOffset: '-6px'
							}
						}}
					>
						<Box className="play-btn" sx={{ transition: 'transform 0.2s ease' }}>
							<PlayIcon />
						</Box>
					</IconButton>
				</Box>

				{hasTextSection && (
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: '1rem',
							mt: { xs: '1.5rem', md: '2rem' },
							[TWO_COL_MEDIA]: {
								flexDirection: 'row',
								gap: '5.5rem'
							}
						}}
					>
						{title && (
							<Box
								sx={{
									minWidth: 0,
									[TWO_COL_MEDIA]: { flex: '1 1 0' }
								}}
							>
								<Typography
									variant="title-md"
									sx={(t) => ({
										fontSize: '1.5rem',
										lineHeight: '2rem',
										letterSpacing: '-0.03em',
										color: 'text.primary',
										[t.breakpoints.up('md')]: {
											fontSize: '2rem',
											lineHeight: '2.5rem',
											letterSpacing: '-0.03em'
										},
										[t.breakpoints.up('lg')]: {
											fontSize: '2rem',
											lineHeight: '2.5rem',
											letterSpacing: '-0.03em'
										}
									})}
								>
									{title}
								</Typography>
							</Box>
						)}

						{description && (
							<Box
								sx={{
									minWidth: 0,
									[TWO_COL_MEDIA]: { flex: '1 1 0' }
								}}
							>
								<Typography variant="body-sm" sx={{ color: 'text.secondary' }}>
									{description}
								</Typography>
							</Box>
						)}
					</Box>
				)}
			</Box>
		</FadeInView>
	);
};

export default VideoBlockV2;
